import { ContactSchema, type PersonContact } from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { CrudForm } from 'components/form';
import {
  type ContactWithOutCoordinates,
  resolveFormContactFields,
} from 'core/forms';
import type { Fetcher } from 'design-system';
import type { FormikErrors } from 'formik';
import { useAddressAutoFill } from 'hooks/useAddressAutoFill';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { initialContact } from 'screens/person';
import { strings } from 'strings';

type OnChange = (
  data: Partial<PersonContact>,
  errors: FormikErrors<Partial<ContactWithOutCoordinates>>,
) => void;

interface ContactFormProps {
  onChange: OnChange;
  showErrors: boolean;
  hasEmergencyAccess?: boolean;
  addressFetcher?: Fetcher<string>;
  getAddressPartial?: (
    placeId: string,
  ) => Promise<Partial<ContactWithOutCoordinates>>;
  initial: Partial<PersonContact>;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onChange,
  showErrors,
  addressFetcher,
  getAddressPartial,
  hasEmergencyAccess,
  initial,
}) => {
  const [formState, setFormState] = useState(initialContact);

  const [initialState, setInitialState] = useState(initial);

  const { onContactChange, placeId } = useAddressAutoFill(
    setFormState,
    getAddressPartial,
  );

  useEffect(() => {
    if (placeId !== initialState.addressPlaceId) {
      setInitialState(() => ({
        ...initialState,
        ...formState,
        addressPlaceId: placeId,
      }));
    }
  }, [placeId, initialState, formState]);

  const handleChange = useCallback<OnChange>(
    (data, errors) => {
      const resolvedData = {
        ...data,
        addressLines: compact(data.addressLines),
        telecomPhoneNumbers: compact(data.telecomPhoneNumbers),
      };

      onContactChange(resolvedData);

      onChange(resolvedData, errors);
    },
    [onChange, onContactChange],
  );

  const contactFields = resolveFormContactFields(
    formState,
    hasEmergencyAccess,
    addressFetcher,
  );

  return (
    <CrudForm<ContactWithOutCoordinates>
      key={initialState.addressPlaceId}
      title={strings.FormLabelPatientContactDetails}
      fields={contactFields}
      showSubmitButton={false}
      initialValues={initialState}
      includeIcons={true}
      validationSchema={ContactSchema}
      onChange={handleChange}
      showErrors={showErrors}
      onChangeDebounceTime={0}
      validateOnMount
    />
  );
};
